// material-ui
import { createTheme } from '@mui/material/styles';

// assets
import defaultColor from 'assets/scss/_themes-vars.module.scss';
import theme1 from 'assets/scss/_theme1.module.scss';
import theme2 from 'assets/scss/_theme2.module.scss';
import theme3 from 'assets/scss/_theme3.module.scss';
import theme4 from 'assets/scss/_theme4.module.scss';
import theme5 from 'assets/scss/_theme5.module.scss';
import theme6 from 'assets/scss/_theme6.module.scss';

// project import
import { brand, semantic } from './tokens';

// ==============================|| DEFAULT THEME - PALETTE  ||============================== //

const Palette = (navType, presetColor) => {
    const isDefaultPreset = !presetColor || presetColor === 'default';
    let colors;
    switch (presetColor) {
        case 'theme1':
            colors = theme1;
            break;
        case 'theme2':
            colors = theme2;
            break;
        case 'theme3':
            colors = theme3;
            break;
        case 'theme4':
            colors = theme4;
            break;
        case 'theme5':
            colors = theme5;
            break;
        case 'theme6':
            colors = theme6;
            break;
        case 'default':
        default:
            colors = defaultColor;
    }

    return createTheme({
        palette: {
            mode: navType,
            common: {
                black: colors.darkPaper
            },
            // Brand roles come from tokens, NOT from the stock Berry SCSS
            // variables. Those still shipped $primaryMain: #2196f3 (material
            // blue) and $secondaryMain: #673ab7 (purple), so anything using
            // MUI's `color="primary"` rendered blue while the styled
            // components rendered navy. Presets theme1-6 keep their own ramps.
            primary: isDefaultPreset
                ? {
                      light: navType === 'dark' ? brand.navy[300] : semantic.primary.light,
                      main: navType === 'dark' ? brand.navy[300] : semantic.primary.main,
                      dark: navType === 'dark' ? brand.navy[200] : semantic.primary.dark,
                      200: brand.navy[200],
                      800: brand.navy[800],
                      contrastText: semantic.primary.contrastText
                  }
                : {
                      light: navType === 'dark' ? colors.darkPrimaryLight : colors.primaryLight,
                      main: navType === 'dark' ? colors.darkPrimaryMain : colors.primaryMain,
                      dark: navType === 'dark' ? colors.darkPrimaryDark : colors.primaryDark,
                      200: navType === 'dark' ? colors.darkPrimary200 : colors.primary200,
                      800: navType === 'dark' ? colors.darkPrimary800 : colors.primary800
                  },
            secondary: isDefaultPreset
                ? {
                      light: semantic.secondary.light,
                      main: navType === 'dark' ? brand.teal[300] : semantic.secondary.main,
                      dark: semantic.secondary.dark,
                      200: brand.teal[200],
                      800: brand.teal[800],
                      contrastText: semantic.secondary.contrastText
                  }
                : {
                      light: navType === 'dark' ? colors.darkSecondaryLight : colors.secondaryLight,
                      main: navType === 'dark' ? colors.darkSecondaryMain : colors.secondaryMain,
                      dark: navType === 'dark' ? colors.darkSecondaryDark : colors.secondaryDark,
                      200: navType === 'dark' ? colors.darkSecondary200 : colors.secondary200,
                      800: navType === 'dark' ? colors.darkSecondary800 : colors.secondary800
                  },
            error: isDefaultPreset
                ? { light: semantic.error.light, main: semantic.error.main, dark: semantic.error.dark }
                : { light: colors.errorLight, main: colors.errorMain, dark: colors.errorDark },
            orange: {
                light: colors.orangeLight,
                main: colors.orangeMain,
                dark: colors.orangeDark
            },
            warning: isDefaultPreset
                ? { light: semantic.warning.light, main: semantic.warning.main, dark: semantic.warning.dark }
                : { light: colors.warningLight, main: colors.warningMain, dark: colors.warningDark },
            success: isDefaultPreset
                ? {
                      light: semantic.success.light,
                      200: brand.forest[200],
                      main: semantic.success.main,
                      dark: semantic.success.dark
                  }
                : { light: colors.successLight, 200: colors.success200, main: colors.successMain, dark: colors.successDark },
            grey: isDefaultPreset
                ? {
                      ...brand.grey,
                      500: navType === 'dark' ? colors.darkTextSecondary : brand.grey[500],
                      600: navType === 'dark' ? colors.darkTextTitle : brand.grey[900],
                      700: navType === 'dark' ? colors.darkTextPrimary : brand.grey[700],
                      900: navType === 'dark' ? colors.darkTextPrimary : brand.grey[900]
                  }
                : {
                      50: colors.grey50,
                      100: colors.grey100,
                      500: navType === 'dark' ? colors.darkTextSecondary : colors.grey500,
                      600: navType === 'dark' ? colors.darkTextTitle : colors.grey900,
                      700: navType === 'dark' ? colors.darkTextPrimary : colors.grey700,
                      900: navType === 'dark' ? colors.darkTextPrimary : colors.grey900
                  },
            dark: {
                light: colors.darkTextPrimary,
                main: colors.darkLevel1,
                dark: colors.darkLevel2,
                800: colors.darkBackground,
                900: colors.darkPaper
            },
            text: {
                primary: navType === 'dark' ? colors.darkTextPrimary : colors.grey700,
                secondary: navType === 'dark' ? colors.darkTextSecondary : colors.grey500,
                dark: navType === 'dark' ? colors.darkTextPrimary : colors.grey900,
                hint: colors.grey100
            },
            // Was `darkTextPrimary` in dark mode - a near-white *text* colour
            // used as a border, which made every divider shout.
            divider: navType === 'dark' ? 'rgba(255, 255, 255, 0.16)' : brand.grey[200],
            background: {
                paper: navType === 'dark' ? colors.darkLevel2 : colors.paper,
                default: navType === 'dark' ? colors.darkPaper : colors.paper
            },
            zimttech: {
                accent: navType === 'dark' ? brand.forest[300] : semantic.success.main,
                brand: navType === 'dark' ? brand.navy[700] : brand.navy[500],
                brandSoft: navType === 'dark' ? 'rgba(111, 143, 216, 0.18)' : brand.navy[50],
                border: navType === 'dark' ? 'rgba(255, 255, 255, 0.16)' : brand.grey[200],
                canvas: navType === 'dark' ? brand.navy[900] : brand.grey[50],
                errorSoft: navType === 'dark' ? 'rgba(244, 114, 114, 0.14)' : brand.red[50],
                highlight: '#E1C84B',
                ink: navType === 'dark' ? brand.grey[50] : brand.grey[900]
            }
        }
    });
};

export default Palette;
